// Native push registration via Firebase Cloud Messaging (the whodat flow).
// FCM hands out tokens on BOTH Android and iOS, so the backend delivers to
// both through one FCM API. No-op on the web. Needs the Firebase config
// files in the native projects (google-services.json /
// GoogleService-Info.plist) to actually fire.
import { Capacitor } from "@capacitor/core";
import { nativePost } from "./nativeHttp";
import { userHeaders, getUserToken } from "./userAuth";
import { getVisitorId } from "./trackPageView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

let registeredFor = null; // "<device token>|<user token>" last sent to the server
let listening = false;

// The person's own push switch (Settings → Allow push notifications). Unset
// until the very first OS permission prompt: its answer becomes the default,
// so the toggle starts out as whatever they chose at install.
const PUSH_PREF_KEY = "appPushEnabled";
export function pushPref() {
  try {
    const v = localStorage.getItem(PUSH_PREF_KEY);
    return v === null ? null : v === "true";
  } catch { return null; }
}
function setPushPref(on) {
  try { localStorage.setItem(PUSH_PREF_KEY, String(on)); } catch {}
}

// A tapped notification can carry an in-app destination in its data ("url").
// The tap can land before AppLayout mounts (a cold start from the
// notification), so the path is parked here and the layout collects it.
let pendingPushUrl = null;
function openPushUrl(path) {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return;
  pendingPushUrl = path;
  window.dispatchEvent(new CustomEvent("push-open-url"));
}
export function consumePendingPushUrl() {
  const p = pendingPushUrl;
  pendingPushUrl = null;
  return p;
}

// Registers the device with the server. A signed-in user's device is keyed
// by their account; a guest's by the browser's visitor id. Re-sent whenever
// the FCM token or the signed-in user changes (registerPush runs on every
// app mount, including right after sign-in).
async function sendToken(token) {
  const key = `${token}|${getUserToken()}`;
  if (registeredFor === key) return;
  registeredFor = key;
  try {
    const res = await nativePost(`${BACKEND_URL}/api/push-token`, {
      token, platform: Capacitor.getPlatform(), visitorId: getVisitorId(),
    }, userHeaders());
    if (!res.ok) registeredFor = null;
  } catch {
    registeredFor = null; // let a later mount retry
  }
}

export async function registerPush() {
  if (!Capacitor.isNativePlatform()) return;
  if (pushPref() === false) return; // turned off in Settings
  try {
    // Dynamic import so the messaging SDK is a native-only lazy chunk, never
    // part of the web bundle's critical path.
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== "granted") {
      console.warn("[push] permission not granted:", perm.receive);
      if (pushPref() === null) setPushPref(false); // their first answer is the default
      return;
    }
    if (pushPref() === null) setPushPref(true);
    const { token } = await FirebaseMessaging.getToken();
    if (!token) {
      console.warn("[push] FCM returned no token");
      return;
    }
    await sendToken(token);
    if (!listening) {
      listening = true;
      // FCM can rotate the token; keep the server copy fresh.
      FirebaseMessaging.addListener("tokenReceived", (e) => { if (e?.token) sendToken(e.token); });
      // Tapping a notification lands the person where the admin pointed it.
      FirebaseMessaging.addListener("notificationActionPerformed", (e) => {
        openPushUrl(String(e?.notification?.data?.url || ""));
      });
    }
  } catch (e) {
    console.warn("[push] registration failed:", e?.message || e);
    registeredFor = null;
  }
}

// Settings → Allow push notifications. On: register this device (returns
// {blocked: true} when the OS permission is denied, so the toggle can say
// why). Off: invalidate the FCM token on the device and drop the server's
// copy, so nothing arrives any more.
export async function setPushEnabled(on) {
  if (!Capacitor.isNativePlatform()) return { ok: false };
  if (on) {
    setPushPref(true);
    try {
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      const perm = await FirebaseMessaging.requestPermissions();
      if (perm.receive !== "granted") {
        setPushPref(false); // the OS says no; an on-looking toggle would lie
        return { ok: false, blocked: true };
      }
      await registerPush();
      return { ok: true };
    } catch (e) {
      console.warn("[push] enable failed:", e?.message || e);
      return { ok: false };
    }
  }
  setPushPref(false);
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const { token } = await FirebaseMessaging.getToken().catch(() => ({ token: null }));
    if (token) {
      await nativePost(`${BACKEND_URL}/api/push-token/remove`, { token }, userHeaders()).catch(() => {});
    }
    await FirebaseMessaging.deleteToken().catch(() => {});
  } catch { /* already unreachable; the pref alone stops re-registration */ }
  registeredFor = null; // a later re-enable registers the fresh token
  return { ok: true };
}
